
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SiteSettings {
  siteName: string;
  siteDescription: string;
  headerLogoUrl: string;
  footerLogoUrl: string;
  footerDescription: string;
  contactEmail: string;
  contactPhone: string;
  contactAddress: string;
  contactWhatsapp: string;
  socialFacebook: string;
  socialInstagram: string;
  socialLinkedin: string;
  socialTwitter: string;
  faviconUrl: string;
}

const defaultSettings: SiteSettings = {
  siteName: "TECNOSYNC",
  siteDescription: "",
  headerLogoUrl: "",
  footerLogoUrl: "",
  footerDescription: "",
  contactEmail: "",
  contactPhone: "",
  contactAddress: "",
  contactWhatsapp: "",
  socialFacebook: "",
  socialInstagram: "",
  socialLinkedin: "",
  socialTwitter: "",
  faviconUrl: ""
};

const fetchSiteSettings = async (): Promise<SiteSettings> => {
  const { data, error } = await supabase
    .from('site_settings')
    .select('*');

  if (error) {
    console.error('Error loading site settings:', error);
    return defaultSettings;
  }

  const settingsMap = data?.reduce((acc: any, setting) => {
    acc[setting.setting_key] = setting.setting_value;
    return acc;
  }, {}) || {};

  return {
    siteName: settingsMap.site_name || defaultSettings.siteName,
    siteDescription: settingsMap.site_description || defaultSettings.siteDescription,
    headerLogoUrl: settingsMap.header_logo_url || settingsMap.logo_url || defaultSettings.headerLogoUrl,
    footerLogoUrl: settingsMap.footer_logo_url || defaultSettings.footerLogoUrl,
    footerDescription: settingsMap.footer_description || defaultSettings.footerDescription,
    contactEmail: settingsMap.contact_email || defaultSettings.contactEmail,
    contactPhone: settingsMap.contact_phone || defaultSettings.contactPhone,
    contactAddress: settingsMap.contact_address || defaultSettings.contactAddress,
    contactWhatsapp: settingsMap.contact_whatsapp || defaultSettings.contactWhatsapp,
    socialFacebook: settingsMap.social_facebook || defaultSettings.socialFacebook,
    socialInstagram: settingsMap.social_instagram || defaultSettings.socialInstagram,
    socialLinkedin: settingsMap.social_linkedin || defaultSettings.socialLinkedin,
    socialTwitter: settingsMap.social_twitter || defaultSettings.socialTwitter,
    faviconUrl: settingsMap.favicon_url || defaultSettings.faviconUrl,
  };
};

export const useSiteSettings = () => {
  const { data: settings, isLoading: loading, refetch } = useQuery({
    queryKey: ['site-settings'],
    queryFn: fetchSiteSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: defaultSettings,
  });

  return { settings: settings || defaultSettings, loading, reloadSettings: refetch };
};
